import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import AddCommentForm from './AddCommentForm';
import {
  createCommentBodyHelper,
  getInitialValuesForComments,
  reverseTransformContent,
  transformRawContent,
} from '../../../../util/circleHelper';
import { extractMentionedUsers } from '../../../../util/mentionHelper';
import { connect, useDispatch } from 'react-redux';
import {
  createCommentForPost,
  editCommentForPost,
  editReplyComment,
  toggelEditComment,
  toggelVideoReducer,
  toggleEmoji,
  toggleCreateComment,
  toggleReplyComment,
} from '../../CircleCommunityPage.duck';
import { compose } from 'redux';
import { CompositeDecorator, EditorState, Modifier, convertFromRaw, convertToRaw } from 'draft-js';
import { checkElephantModalOpen } from '../../../../util/data';

const mentionStrategy = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  let matchArr, start;
  const regex = /@\w+/g; // Detect mentions starting with '@'
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};
const Mention = props => (
  <span style={{ backgroundColor: '#e8f5ff', color: '#0073e6' }}>{props.children}</span>
);

const compositeDecorator = new CompositeDecorator([
  {
    strategy: mentionStrategy,
    component: Mention,
  },
]);

const COMMENT_MEDIA_INITIAL_VALUE_FIELDS = [
  'image',
  'video',
  'document',
  'gifs',
  'imagefiles',
  'videofiles',
  'documentsfiles',
  'giffiles',
];

const hasCommentInitialMediaValues = values =>
  COMMENT_MEDIA_INITIAL_VALUE_FIELDS.some(field => {
    const fieldValue = values?.[field];
    return Array.isArray(fieldValue) && fieldValue.length > 0;
  });

const AddCommentContainer = props => {
  const {
    onVideoClick,
    isVideoUpload,
    onEmojiClick,
    isEmojiOpen,
    onAdvancedSearch,
    advancedSearchData,
    currentUser,
    usersSubscriptionMap = {},
    usersStripeConnectionMap = {},
    isBasicRole = false,
    editCommentData = null,
    // setIsSecondModalOpen,
  } = props;
  const dispatch = useDispatch();
  const [isCommentImageModalOpen, setIsCommentImageModalOpen] = useState(false);
  const [isCommentGifModalOpen, setIsCommentGifModalOpen] = useState(false);
  const [isCommentDocumentModalOpen, setIsCommentDocumentModalOpen] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty(compositeDecorator));
  const {
    currentCommunityMember,
    postId,
    spaceId,
    spaceName,
    spaceSlug,
    requestCreateComment,
    requestDirectUpload,
    onSetElephantVerificationModal = () => {},
    handleCloseFeedModal = () => {},
  } = props;
  const { avatar_url, name, headline } = currentCommunityMember || {};
  const isEditMode = !!editCommentData;
  const { id: editCommentId, post_id: editPostId, parent_comment_id: parentCommentId } =
    editCommentData || {};
  const editFormKey = isEditMode
    ? `edit-comment-${parentCommentId ? `reply-${parentCommentId}-` : ''}${editCommentId || 'new'}`
    : 'create-comment';
  const cachedEditMediaValuesRef = useRef({});
  const rawEditInitialValues = useMemo(
    () => (isEditMode ? getInitialValuesForComments(editCommentData) : {}),
    [editCommentData, isEditMode]
  );
  const editInitialValues = useMemo(() => {
    if (!isEditMode) {
      return {};
    }

    if (hasCommentInitialMediaValues(rawEditInitialValues)) {
      return rawEditInitialValues;
    }

    return cachedEditMediaValuesRef.current[editFormKey] || rawEditInitialValues;
  }, [editFormKey, isEditMode, rawEditInitialValues]);

  // Check if current user has active subscription or stripe connection
  const currentUserHasActiveSubscription =
    (currentUser?.id?.uuid && usersSubscriptionMap[currentUser.id.uuid]) || isBasicRole;
  const currentUserHasStripeConnected =
    (currentUser?.id?.uuid && usersStripeConnectionMap[currentUser.id.uuid]) || false;

  useEffect(() => {
    if (!isEditMode) {
      setEditorState(EditorState.createEmpty(compositeDecorator));
      cachedEditMediaValuesRef.current = {};
      return;
    }

    const rawContent = reverseTransformContent(editCommentData?.tiptap_body?.body);
    setEditorState(EditorState.createWithContent(convertFromRaw(rawContent), compositeDecorator));
  }, [editCommentId, isEditMode]);

  useEffect(() => {
    if (isEditMode && hasCommentInitialMediaValues(rawEditInitialValues)) {
      cachedEditMediaValuesRef.current[editFormKey] = rawEditInitialValues;
    }
  }, [editFormKey, isEditMode, rawEditInitialValues]);

  const clearComposer = () => {
    setEditorState(EditorState.createEmpty(compositeDecorator));
    dispatch(toggelEditComment());
    dispatch(toggleReplyComment());
    dispatch(toggleCreateComment(false));
  };

  const handleCancelEdit = () => {
    clearComposer();
  };

  const toggleCommentImageModal = useCallback(() => {
    setIsCommentImageModalOpen(current => !current);
  }, []);

  const toggleCommentGifModal = useCallback(() => {
    setIsCommentGifModalOpen(current => !current);
  }, []);

  const toggleCommentDocumentModal = useCallback(() => {
    setIsCommentDocumentModalOpen(current => !current);
  }, []);

  const handleCommentCreation = async values => {
    try {
      const isNotPermission = checkElephantModalOpen(currentUser);
      if (isNotPermission) {
        handleCloseFeedModal();
        onSetElephantVerificationModal(true);
        return;
      }

      const contentState = editorState?.getCurrentContent();
      const rawContent = convertToRaw(contentState); // Convert to raw JS object
      const formateEditor = transformRawContent(rawContent);
      const params = createCommentBodyHelper(values, formateEditor);

      // Extract mentioned users from the comment
      const mentionedUserSgids = extractMentionedUsers(rawContent);

      // Pass notification parameters along with the comment
      const notificationParams = {
        spaceId,
        spaceName,
        spaceSlug,
        commenterUserId: currentUser?.id?.uuid, // Use uuid as the unique user identifier
        commenterName: name,
        mentionedUserSgids, // Pass the mentioned users
      };

      const response = isEditMode
        ? parentCommentId
          ? await dispatch(editReplyComment(params, parentCommentId, editCommentId))
          : await dispatch(editCommentForPost(params, editPostId || postId, editCommentId))
        : await dispatch(createCommentForPost(params, postId, notificationParams));

      // Only clear the editor if the comment was created successfully
      if (response && !response.error) {
        setEditorState(EditorState.createEmpty(compositeDecorator));
        if (isEditMode) {
          clearComposer();
        }
        if (typeof window !== 'undefined') {
          const el = document.getElementById('requestCreateComment');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          }
        }
        return response; // Return response to indicate success
      } else {
        // API returned an error response
        console.error('Failed to create comment:', response?.error || 'Unknown error');
        return response?.error; // Return null to indicate failure
      }
    } catch (error) {
      // Don't clear the editor on error, so user can retry
      console.error('Failed to create comment:', error);
      return error; // Return null to indicate failure
    }
  };

  const insertEmoji = emoji => {
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    // Make sure the insertion is at the cursor's current position
    if (!selection.isCollapsed()) {
      const newContentState = Modifier.replaceText(currentContent, selection, emoji);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    } else {
      // If the selection is collapsed (no text selected), append the emoji
      const newContentState = Modifier.insertText(currentContent, selection, emoji);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    }
    onEmojiClick(false);
  };

  return (
    <div>
      <AddCommentForm
        key={editFormKey}
        currentUser={currentUser}
        initialValues={editInitialValues}
        initialValuesEqual={() => true}
        editInitialValues={editInitialValues}
        editFormKey={editFormKey}
        editorState={editorState}
        setEditorState={setEditorState}
        insertEmoji={insertEmoji}
        onSubmit={handleCommentCreation}
        requestCreateComment={requestCreateComment}
        requestDirectUpload={requestDirectUpload}
        onGiphyChange={toggleCommentGifModal}
        onDocumentClick={toggleCommentDocumentModal}
        onImageClick={toggleCommentImageModal}
        onVideoClick={onVideoClick}
        isGiphyOpen={isCommentGifModalOpen}
        isDocumentModal={isCommentDocumentModalOpen}
        isImageModal={isCommentImageModalOpen}
        isVideoUpload={isVideoUpload}
        avatar_url={avatar_url}
        onEmojiClick={onEmojiClick}
        isEmojiOpen={isEmojiOpen}
        name={name}
        onAdvancedSearch={onAdvancedSearch}
        advancedSearchData={advancedSearchData}
        currentUserName={name}
        current_avatar_url={avatar_url}
        onSetElephantVerificationModal={onSetElephantVerificationModal}
        currentUserHasActiveSubscription={currentUserHasActiveSubscription}
        currentUserHasStripeConnected={currentUserHasStripeConnected}
        isEditMode={isEditMode}
        onCancelEdit={handleCancelEdit}
        submitLabel={isEditMode ? 'Update' : 'Post'}
      />
    </div>
  );
};

const mapStateToProps = state => {
  const {
    isVideoUpload,
    isEmojiOpen,
    usersSubscriptionMap,
    usersStripeConnectionMap,
  } = state.CircleCommunityPage;

  return {
    isVideoUpload,
    isEmojiOpen,
    usersSubscriptionMap,
    usersStripeConnectionMap,
  };
};
const mapDispatchToProps = dispatch => ({
  onVideoClick: () => dispatch(toggelVideoReducer()),
  onEmojiClick: params => dispatch(toggleEmoji(params)),
});
export default compose(connect(mapStateToProps, mapDispatchToProps))(AddCommentContainer);